import { useState, useEffect, useRef, useCallback } from "react";

const LIVEBRIDGE_RESTART_DELAY_MS = 260;

function isFatalRecognitionError(errorCode) {
  return (
    errorCode === "not-allowed" ||
    errorCode === "service-not-allowed" ||
    errorCode === "audio-capture"
  );
}

function shouldAutoRecoverRecognition(errorCode) {
  return (
    !isFatalRecognitionError(errorCode) &&
    (errorCode === "" ||
      errorCode === "no-speech" ||
      errorCode === "aborted" ||
      errorCode === "network")
  );
}

/**
 * useRecognition
 *
 * Owns the full speech recognition lifecycle including the restart timer.
 * All four previous scatter points for liveBridgeRestartTimerRef are now
 * in one place — the unified clearRestartTimer() helper used everywhere.
 *
 * Props:
 *   page                    — current app page string
 *   t                       — i18n function
 *   onRecognitionFinal      — (transcript, languageCode) => void
 *   onRecognitionInterim    — (transcript, languageCode) => void
 *   commitPendingRecognitionText — (languageCode) => void
 */
export function useRecognition({
  page,
  t,
  onRecognitionFinal,
  onRecognitionInterim,
  commitPendingRecognitionText
}) {
  const liveBridgeSpeechReadyText = t("livebridge_speech_ready");
  const liveBridgeSpeechFallbackText = t("livebridge_speech_note");
  const liveBridgeSpeechUnavailableText = t("livebridge_speech_unavailable");

  const [liveBridgeSpeechSupported, setLiveBridgeSpeechSupported] = useState(false);
  const [liveBridgeRecognitionLanguage, setLiveBridgeRecognitionLanguage] = useState("en-US");
  const [liveBridgeSpeechHint, setLiveBridgeSpeechHint] = useState("");
  const [liveBridgeInputStatus, setLiveBridgeInputStatus] = useState("idle");
  const [liveBridgeRecordingSeconds, setLiveBridgeRecordingSeconds] = useState(0);

  const liveBridgeRecognitionRef = useRef(null);
  const liveBridgeListeningDesiredRef = useRef(false);
  const liveBridgeRecognitionLanguageRef = useRef("en-US");
  const liveBridgePageRef = useRef(page);
  const liveBridgeFatalRecognitionErrorRef = useRef(false);
  const liveBridgeRecordingStartedAtRef = useRef(0);
  const liveBridgeRecordingIntervalRef = useRef(null);

  // ─── UNIFIED RESTART TIMER ───────────────────────────────────────────────
  // Previously scattered across 4 call sites. All timer management
  // now flows through these two helpers — no stale handles possible.
  const liveBridgeRestartTimerRef = useRef(null);

  const clearRestartTimer = useCallback(() => {
    if (liveBridgeRestartTimerRef.current) {
      window.clearTimeout(liveBridgeRestartTimerRef.current);
      liveBridgeRestartTimerRef.current = null;
    }
  }, []);

  const scheduleRestart = useCallback(
    (recognition, languageCode) => {
      clearRestartTimer();
      liveBridgeRestartTimerRef.current = window.setTimeout(() => {
        if (
          !liveBridgeListeningDesiredRef.current ||
          liveBridgePageRef.current !== "liveBridgeInput"
        ) {
          return;
        }

        try {
          recognition.lang = languageCode;
          recognition.start();
        } catch (_retryError) {
          setLiveBridgeSpeechHint(liveBridgeSpeechFallbackText);
        }
      }, LIVEBRIDGE_RESTART_DELAY_MS);
    },
    [clearRestartTimer, liveBridgeSpeechFallbackText]
  );
  // ─────────────────────────────────────────────────────────────────────────

  // Keep page ref current so callbacks don't close over stale page value.
  useEffect(() => {
    liveBridgePageRef.current = page;
  }, [page]);

  // Recording timer — runs only while listening.
  useEffect(() => {
    if (liveBridgeInputStatus !== "listening") {
      setLiveBridgeRecordingSeconds(0);

      if (liveBridgeRecordingIntervalRef.current) {
        window.clearInterval(liveBridgeRecordingIntervalRef.current);
        liveBridgeRecordingIntervalRef.current = null;
      }

      return undefined;
    }

    liveBridgeRecordingStartedAtRef.current = Date.now();
    setLiveBridgeRecordingSeconds(0);

    const syncRecordingSeconds = () => {
      setLiveBridgeRecordingSeconds(
        Math.max(0, Math.floor((Date.now() - liveBridgeRecordingStartedAtRef.current) / 1000))
      );
    };

    syncRecordingSeconds();
    liveBridgeRecordingIntervalRef.current = window.setInterval(syncRecordingSeconds, 1000);

    return () => {
      if (liveBridgeRecordingIntervalRef.current) {
        window.clearInterval(liveBridgeRecordingIntervalRef.current);
        liveBridgeRecordingIntervalRef.current = null;
      }
    };
  }, [liveBridgeInputStatus]);

  // Stop listening when navigating away from input page.
  useEffect(() => {
    if (page === "liveBridgeInput") {
      return;
    }

    if (liveBridgeInputStatus === "listening") {
      liveBridgeListeningDesiredRef.current = false;
      commitPendingRecognitionText(liveBridgeRecognitionLanguageRef.current);

      try {
        liveBridgeRecognitionRef.current?.stop();
      } catch (_error) {
        // Ignore repeated stop calls while leaving the page.
      }

      setLiveBridgeInputStatus("idle");
    }
  }, [commitPendingRecognitionText, page, liveBridgeInputStatus]);

  // Speech recognition setup — runs once on mount.
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const BrowserSpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!BrowserSpeechRecognition) {
      setLiveBridgeSpeechSupported(false);
      setLiveBridgeSpeechHint(liveBridgeSpeechUnavailableText);
      return undefined;
    }

    setLiveBridgeSpeechSupported(true);
    setLiveBridgeSpeechHint(liveBridgeSpeechReadyText);

    const recognition = new BrowserSpeechRecognition();
    const prefersContinuousRecognition = !window.webkitSpeechRecognition;
    recognition.lang = liveBridgeRecognitionLanguageRef.current;
    recognition.continuous = prefersContinuousRecognition;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      liveBridgeFatalRecognitionErrorRef.current = false;
      clearRestartTimer();
      setLiveBridgeInputStatus("listening");
      setLiveBridgeConnectionStatus_noop(); // connection status owned by useViewerSession
    };

    recognition.onerror = (event) => {
      const errorCode = String(event?.error || "").trim().toLowerCase();
      const isFatalError = isFatalRecognitionError(errorCode);

      liveBridgeFatalRecognitionErrorRef.current = isFatalError;

      if (isFatalError) {
        liveBridgeListeningDesiredRef.current = false;
      }

      setLiveBridgeInputStatus("idle");
      setLiveBridgeSpeechHint(
        errorCode === "not-allowed" || errorCode === "service-not-allowed"
          ? liveBridgeSpeechUnavailableText
          : shouldAutoRecoverRecognition(errorCode)
          ? liveBridgeSpeechReadyText
          : liveBridgeSpeechFallbackText
      );
    };

    recognition.onend = () => {
      commitPendingRecognitionText(
        liveBridgeRecognitionLanguageRef.current || recognition.lang || "en-US"
      );
      setLiveBridgeInputStatus("idle");

      if (
        liveBridgeListeningDesiredRef.current &&
        liveBridgePageRef.current === "liveBridgeInput" &&
        !liveBridgeFatalRecognitionErrorRef.current
      ) {
        scheduleRestart(recognition, liveBridgeRecognitionLanguageRef.current);
      }
    };

    recognition.onresult = (event) => {
      let latestInterim = "";
      const activeLanguage = String(
        liveBridgeRecognitionLanguageRef.current || recognition.lang || "en-US"
      ).toLowerCase();

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = String(event.results[index]?.[0]?.transcript || "").trim();

        if (!transcript) {
          continue;
        }

        if (event.results[index].isFinal) {
          onRecognitionFinal(transcript, activeLanguage);
        } else {
          latestInterim = transcript;
        }
      }

      onRecognitionInterim(latestInterim, activeLanguage);
    };

    liveBridgeRecognitionRef.current = recognition;

    return () => {
      liveBridgeListeningDesiredRef.current = false;
      liveBridgeFatalRecognitionErrorRef.current = false;
      commitPendingRecognitionText(
        liveBridgeRecognitionLanguageRef.current || recognition.lang || "en-US"
      );
      clearRestartTimer(); // ← always clear before stopping — prevents ghost restarts on unmount
      try {
        recognition.stop();
      } catch (_error) {
        // Ignore cleanup stop errors.
      }
      liveBridgeRecognitionRef.current = null;
    };
  }, [
    clearRestartTimer,
    commitPendingRecognitionText,
    liveBridgeSpeechFallbackText,
    liveBridgeSpeechReadyText,
    liveBridgeSpeechUnavailableText,
    onRecognitionFinal,
    onRecognitionInterim,
    scheduleRestart
  ]);

  // Public: start listening in a given language.
  const handleLiveBridgeStartListening = useCallback(
    (languageCode = "en-US") => {
      const recognition = liveBridgeRecognitionRef.current;

      if (!recognition || !liveBridgeSpeechSupported) {
        setLiveBridgeSpeechHint(t("livebridge_speech_unavailable"));
        return;
      }

      liveBridgeListeningDesiredRef.current = true;
      liveBridgeFatalRecognitionErrorRef.current = false;
      liveBridgeRecognitionLanguageRef.current = languageCode;
      setLiveBridgeRecognitionLanguage(languageCode);
      setLiveBridgeSpeechHint("");

      try {
        recognition.lang = languageCode;
        recognition.start();
      } catch (error) {
        const errorName = String(error?.name || "").trim();

        if (errorName === "InvalidStateError") {
          try {
            recognition.stop();
          } catch (_stopError) {
            // Ignore.
          }
          scheduleRestart(recognition, languageCode);
        } else {
          setLiveBridgeSpeechHint(liveBridgeSpeechUnavailableText);
        }
      }
    },
    [liveBridgeSpeechSupported, liveBridgeSpeechUnavailableText, scheduleRestart, t]
  );

  // Public: stop listening.
  const handleLiveBridgeStopListening = useCallback(() => {
    liveBridgeListeningDesiredRef.current = false;
    liveBridgeFatalRecognitionErrorRef.current = false;
    commitPendingRecognitionText(liveBridgeRecognitionLanguageRef.current);
    clearRestartTimer();

    try {
      liveBridgeRecognitionRef.current?.stop();
    } catch (_error) {
      // Ignore repeated stop calls.
    }

    setLiveBridgeInputStatus("idle");
  }, [clearRestartTimer, commitPendingRecognitionText]);

  const liveBridgeRecordingLabel =
    liveBridgeInputStatus === "listening"
      ? `${String(Math.floor(liveBridgeRecordingSeconds / 60)).padStart(2, "0")}:${String(
          liveBridgeRecordingSeconds % 60
        ).padStart(2, "0")}`
      : "";

  const liveBridgeListeningLabel =
    liveBridgeInputStatus === "listening"
      ? t("livebridge_status_listening")
      : t("livebridge_status_idle");

  return {
    liveBridgeSpeechSupported,
    liveBridgeRecognitionLanguage,
    liveBridgeSpeechHint,
    liveBridgeInputStatus,
    liveBridgeRecordingSeconds,
    liveBridgeRecordingLabel,
    liveBridgeListeningLabel,
    handleLiveBridgeStartListening,
    handleLiveBridgeStopListening
  };
}

// Intentional no-op — connection status is owned by useViewerSession.
// Recognition does not set it directly; the compositor wires this if needed.
function setLiveBridgeConnectionStatus_noop() {}
